import { Component, OnDestroy, OnInit } from '@angular/core'
import { collection, getDocs, limit, orderBy, query, where } from '@angular/fire/firestore'
import { FormControl, FormGroup } from '@angular/forms'
import { BroadLocation, FirestoreStructure } from '@parkupp/core'
import { AdminService } from 'src/app/services/admin.service'
import {
    NationalHeadline, CityRow, SuburbRow, AmenityPremium,
    PriceBandRow, AskingVsAchieved, LeadReasonRow,
    MonthlyUptake, CityMonthlyUptake, TermMixRow, RenewalRow,
    NonRenewalReasonRow, BlockChart, PRICE_BANDS, CHART_PALETTE,
} from './market-insights.types'
import {
    normaliseParkings, normaliseSubscriptions, normaliseLeads, normaliseSearches,
    NormalisedParking, NormalisedSubscription, NormalisedLead, NormalisedSearch,
    computeNationalHeadline, computeCityRows,
    computePriceBands, computeSuburbLeague, computeAmenityPremiums, computeAskingVsAchieved,
    computeLeadReasons, computeMonthlyUptake, computeCityMonthlyUptake, computeSeasonality,
    computeTermMix, computeRenewalRates, computeNonRenewalReasons, computeContractDurationDistribution,
    computeSearchVolumeByCity,
} from './market-insights.calculations'
import { getCanonicalMetros } from './city-normalisation'

@Component({
    selector: 'app-admin-market-insights',
    templateUrl: './admin-market-insights.component.html',
    styleUrls: ['./admin-market-insights.component.scss'],
})
export class AdminMarketInsightsComponent implements OnInit, OnDestroy {
    isLoading = true
    loadingMessage = 'Loading parkings...'

    filterForm: FormGroup = new FormGroup({
        filterDateRange: new FormControl('alltime'),
        customStartDate: new FormControl(''),
        customEndDate: new FormControl(''),
        selectedCities: new FormControl([]),
        minSampleSize: new FormControl(3),
    })

    dateRangeOptions = [
        { value: 'thismonth', label: 'This month' },
        { value: 'lastmonth', label: 'Last month' },
        { value: 'last3months', label: 'Last 3 months' },
        { value: 'last6months', label: 'Last 6 months' },
        { value: 'thisyear', label: 'This year' },
        { value: 'last12months', label: 'Last 12 months' },
        { value: 'alltime', label: 'All time' },
        { value: 'custom', label: 'Custom range' },
    ]

    allCities: string[] = getCanonicalMetros()

    // Raw normalised data (cached)
    private allParkings: NormalisedParking[] = []
    private allSubs: NormalisedSubscription[] = []
    private allLeads: NormalisedLead[] = []
    private allSearches: NormalisedSearch[] = []
    unresolvedCount = 0

    // Computed data
    headline: NationalHeadline | null = null
    cityRows: CityRow[] = []
    priceBandRows: PriceBandRow[] = []
    suburbMost: SuburbRow[] = []
    suburbLeast: SuburbRow[] = []
    amenityPremiums: AmenityPremium[] = []
    askingVsAchieved: AskingVsAchieved[] = []
    leadReasons: LeadReasonRow[] = []
    monthlyUptake: MonthlyUptake[] = []
    cityMonthlyUptake: CityMonthlyUptake[] = []
    seasonality: { month: string; count: number }[] = []
    termMix: TermMixRow[] = []
    renewalRates: RenewalRow[] = []
    nonRenewalReasons: NonRenewalReasonRow[] = []
    durationDistribution: { duration: string; count: number }[] = []

    // Charts
    priceComparisonChart: BlockChart = { series: [], categories: [] }
    contractSizeChart: BlockChart = { series: [], categories: [] }
    amenityPremiumChart: BlockChart = { series: [], categories: [] }
    askingVsAchievedChart: BlockChart = { series: [], categories: [] }
    uptakeChart: BlockChart = { series: [], categories: [] }
    seasonalityChart: BlockChart = { series: [], categories: [] }
    leadReasonsChart: BlockChart = { series: [], categories: [] }
    termMixChart: BlockChart = { series: [], categories: [] }
    durationChart: BlockChart = { series: [], categories: [] }
    nonRenewalChart: BlockChart = { series: [], categories: [] }
    priceBandChart: BlockChart = { series: [], categories: [] }

    // Table sort
    citySortColumn: keyof CityRow = 'totalBays'
    citySortDirection: 'asc' | 'desc' = 'desc'

    priceBandLabels = PRICE_BANDS.map((b) => b.label)

    readonly dataLabelsPremium = {
        enabled: true,
        formatter: (val: number) => (val > 0 ? '+' + val + '%' : val + '%'),
        style: { fontSize: '13px', fontWeight: 700 },
    }

    private destroyed = false

    constructor(private adminSvc: AdminService) {}

    async ngOnInit(): Promise<void> {
        await this.loadAllData()
        this.filterForm.valueChanges.subscribe(() => {
            this.recompute()
        })
    }

    ngOnDestroy(): void {
        this.destroyed = true
    }

    get isCustomDateRange(): boolean {
        return this.filterForm.get('filterDateRange')?.value === 'custom'
    }

    get minSampleSize(): number {
        return this.filterForm.get('minSampleSize')?.value || 3
    }

    get selectedCities(): string[] {
        return this.filterForm.get('selectedCities')?.value || []
    }

    async loadAllData(): Promise<void> {
        this.isLoading = true

        try {
            this.loadingMessage = 'Loading parkings...'
            const parkingsSnap = await getDocs(query(collection(this.adminSvc.firestore, FirestoreStructure.PARKINGS)))
            const rawParkings = parkingsSnap.docs.map((d) => ({ ...d.data(), $key: d.id }))

            if (this.destroyed) return

            this.loadingMessage = 'Loading subscriptions...'
            const subsSnap = await getDocs(query(collection(this.adminSvc.firestore, FirestoreStructure.SUBSCRIPTIONS)))
            const rawSubs = subsSnap.docs.map((d) => ({ ...d.data(), $key: d.id }))

            if (this.destroyed) return

            this.loadingMessage = 'Loading leads...'
            const leadsSnap = await getDocs(query(collection(this.adminSvc.firestore, FirestoreStructure.LEADS), orderBy('createdAt', 'desc')))
            const rawLeads = leadsSnap.docs.map((d) => ({ ...d.data(), $key: d.id }))

            if (this.destroyed) return

            this.loadingMessage = 'Loading search logs...'
            const searchesSnap = await getDocs(query(
                collection(this.adminSvc.firestore, FirestoreStructure.LOG_SEARCHES_PATH),
                orderBy('createdAt', 'desc'),
                limit(30000)
            ))
            const rawSearches = searchesSnap.docs.map((d) => d.data())

            if (this.destroyed) return

            // Normalise
            this.loadingMessage = 'Crunching numbers...'
            const { parkings, unresolvedCount } = normaliseParkings(rawParkings)
            this.allParkings = parkings
            this.unresolvedCount = unresolvedCount

            const parkingMetroMap = new Map<string, string>()
            for (const p of parkings) {
                if (p.raw.$key) parkingMetroMap.set(p.raw.$key, p.metro)
            }

            this.allSubs = normaliseSubscriptions(rawSubs, parkingMetroMap)
            this.allLeads = normaliseLeads(rawLeads)
            this.allSearches = normaliseSearches(rawSearches)

            this.recompute()
        } catch (err) {
            console.error('Market Insights: failed to load data', err)
            this.loadingMessage = 'Failed to load data. Check console.'
        }
    }

    async refreshData(): Promise<void> {
        await this.loadAllData()
    }

    private recompute(): void {
        const dateStart = this.getDateRangeStart(this.filterForm.get('filterDateRange')?.value)
        const dateEnd = this.getDateRangeEnd(this.filterForm.get('filterDateRange')?.value)
        const cities = this.selectedCities

        const filterByDate = <T extends { createdAt: Date | null }>(items: T[]): T[] => {
            return items.filter((item) => {
                if (!item.createdAt) return true
                if (dateStart && item.createdAt < dateStart) return false
                if (dateEnd && item.createdAt > dateEnd) return false
                return true
            })
        }

        const filterByCity = <T extends { metro: string }>(items: T[]): T[] => {
            if (!cities || cities.length === 0) return items
            return items.filter((item) => cities.includes(item.metro))
        }

        // Parkings are not date-filtered (supply is current state), only city-filtered
        const parkings = filterByCity(this.allParkings)
        const subs = filterByCity(filterByDate(this.allSubs))
        const leads = filterByCity(filterByDate(this.allLeads))
        const searches = filterByCity(filterByDate(this.allSearches))

        // A. National headline
        this.headline = computeNationalHeadline(parkings, subs)

        // B. City comparison
        this.cityRows = computeCityRows(parkings, subs, searches, leads, this.minSampleSize)
        this.applyCitySort()
        this.buildCityCharts()

        // C. Pricing
        this.priceBandRows = computePriceBands(parkings)
        const suburbs = computeSuburbLeague(parkings)
        this.suburbMost = suburbs.most
        this.suburbLeast = suburbs.least
        this.amenityPremiums = computeAmenityPremiums(parkings)
        this.askingVsAchieved = computeAskingVsAchieved(parkings, subs)
        this.buildPricingCharts()

        // D. Demand
        this.leadReasons = computeLeadReasons(leads)
        this.buildDemandCharts()

        // E. Uptake
        this.monthlyUptake = computeMonthlyUptake(subs)
        const topCities = this.cityRows.slice(0, 5).map((r) => r.city)
        this.cityMonthlyUptake = computeCityMonthlyUptake(subs, topCities)
        this.seasonality = computeSeasonality(subs)
        this.buildUptakeCharts()

        // F. Commitment
        this.termMix = computeTermMix(parkings)
        this.renewalRates = computeRenewalRates(subs)
        this.nonRenewalReasons = computeNonRenewalReasons(subs)
        this.durationDistribution = computeContractDurationDistribution(subs)
        this.buildCommitmentCharts()

        this.isLoading = false
    }

    // ─── Chart builders ───

    private buildCityCharts(): void {
        const rows = this.cityRows.filter((r) => r.city !== BroadLocation.OTHER && r.sampleSizeParkings >= this.minSampleSize)

        this.priceComparisonChart = {
            series: [
                { name: 'Median Price', data: rows.map((r) => Math.round(r.medianPrice)) },
                { name: 'P25', data: rows.map((r) => Math.round(r.p25Price)) },
                { name: 'P75', data: rows.map((r) => Math.round(r.p75Price)) },
            ],
            categories: rows.map((r) => r.city),
            colors: ['#3e8bff', '#94a3b8', '#94a3b8'],
        }

        this.contractSizeChart = {
            series: [
                { name: 'Avg Months', data: rows.map((r) => +r.avgContractMonths.toFixed(1)) },
                { name: 'Avg Bays', data: rows.map((r) => +r.avgBaysPerContract.toFixed(1)) },
            ],
            categories: rows.map((r) => r.city),
            colors: ['#6366f1', '#14b8a6'],
        }
    }

    private buildPricingCharts(): void {
        this.amenityPremiumChart = {
            series: [{ name: 'Premium %', data: this.amenityPremiums.map((a) => +a.premiumPct.toFixed(1)) }],
            categories: this.amenityPremiums.map((a) => a.label),
            colors: this.amenityPremiums.map((a) => a.premiumPct > 0 ? '#4bd181' : '#ef4444'),
        }

        if (this.askingVsAchieved.length > 0) {
            this.askingVsAchievedChart = {
                series: [
                    { name: 'Asking (Listed)', data: this.askingVsAchieved.map((a) => Math.round(a.askingMedian)) },
                    { name: 'Achieved (Transacted)', data: this.askingVsAchieved.map((a) => Math.round(a.achievedMedian)) },
                ],
                categories: this.askingVsAchieved.map((a) => a.city),
                colors: ['#94a3b8', '#3e8bff'],
            }
        }

        const priceBandCities = this.priceBandRows.filter((r) => r.city !== BroadLocation.OTHER).slice(0, 8)
        this.priceBandChart = {
            series: this.priceBandLabels.map((label, i) => ({
                name: label,
                data: priceBandCities.map((r) => r.bands[i]),
            })),
            categories: priceBandCities.map((r) => r.city),
            colors: ['#4bd181', '#3e8bff', '#f59e0b', '#ef4444', '#a78bfa'],
        }
    }

    private buildDemandCharts(): void {
        const rows = this.leadReasons.filter((r) => r.city !== BroadLocation.OTHER).slice(0, 8)
        this.leadReasonsChart = {
            series: [
                { name: 'Too Far', data: rows.map((r) => r.tooFar) },
                { name: 'Too Expensive', data: rows.map((r) => r.tooExpensive) },
                { name: 'Requirements Not Met', data: rows.map((r) => r.requirementsNotMet) },
                { name: 'Fully Booked', data: rows.map((r) => r.fullyBooked) },
                { name: 'Other', data: rows.map((r) => r.other) },
            ],
            categories: rows.map((r) => r.city),
            colors: ['#f97316', '#ef4444', '#a78bfa', '#3e8bff', '#94a3b8'],
        }
    }

    private buildUptakeCharts(): void {
        if (this.monthlyUptake.length > 1) {
            this.uptakeChart = {
                series: [
                    { name: 'New Contracts', data: this.monthlyUptake.map((m) => m.newContracts) },
                    { name: 'Bays Contracted', data: this.monthlyUptake.map((m) => m.baysContracted) },
                ],
                categories: this.monthlyUptake.map((m) => m.label),
                colors: ['#3e8bff', '#4bd181'],
            }
        }

        this.seasonalityChart = {
            series: [{ name: 'Contracts', data: this.seasonality.map((s) => s.count) }],
            categories: this.seasonality.map((s) => s.month),
            colors: ['#6366f1'],
        }
    }

    private buildCommitmentCharts(): void {
        const termRows = this.termMix.filter((r) => r.city !== BroadLocation.OTHER)
        this.termMixChart = {
            series: [
                { name: 'Fixed Term', data: termRows.map((r) => r.fixedTerm) },
                { name: 'Month-to-Month', data: termRows.map((r) => r.monthToMonth) },
                { name: 'Allocated', data: termRows.map((r) => r.allocated) },
            ],
            categories: termRows.map((r) => r.city),
            colors: ['#3e8bff', '#4bd181', '#f59e0b'],
        }

        this.durationChart = {
            series: [{ name: 'Contracts', data: this.durationDistribution.map((d) => d.count) }],
            categories: this.durationDistribution.map((d) => d.duration),
            colors: ['#6366f1'],
        }

        if (this.nonRenewalReasons.length > 0) {
            this.nonRenewalChart = {
                series: [{ name: 'Count', data: this.nonRenewalReasons.map((r) => r.count) }],
                categories: this.nonRenewalReasons.map((r) => r.label),
                colors: this.nonRenewalReasons.map((_, i) => CHART_PALETTE[i % CHART_PALETTE.length]),
            }
        }
    }

    // ─── Table sorting ───

    sortCityTable(column: keyof CityRow): void {
        if (this.citySortColumn === column) {
            this.citySortDirection = this.citySortDirection === 'asc' ? 'desc' : 'asc'
        } else {
            this.citySortColumn = column
            this.citySortDirection = 'desc'
        }
        this.applyCitySort()
    }

    private applyCitySort(): void {
        this.cityRows = [...this.cityRows].sort((a, b) => {
            const aVal = a[this.citySortColumn] as number
            const bVal = b[this.citySortColumn] as number
            return this.citySortDirection === 'asc' ? aVal - bVal : bVal - aVal
        })
    }

    isBelowThreshold(row: CityRow): boolean {
        return row.sampleSizeParkings < this.minSampleSize
    }

    isFinite(value: number): boolean {
        return Number.isFinite(value)
    }

    // ─── Date range helpers ───

    getDateRangeStart(dateRange: string): Date | null {
        const now = new Date()
        switch (dateRange) {
            case 'thismonth': return new Date(now.getFullYear(), now.getMonth(), 1)
            case 'lastmonth': return new Date(now.getFullYear(), now.getMonth() - 1, 1)
            case 'last3months': return new Date(now.getFullYear(), now.getMonth() - 3, now.getDate())
            case 'last6months': return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate())
            case 'last12months': return new Date(now.getFullYear(), now.getMonth() - 12, now.getDate())
            case 'thisyear': return new Date(now.getFullYear(), 0, 1)
            case 'custom':
                const customStart = this.filterForm.get('customStartDate')?.value
                return customStart ? new Date(customStart) : null
            case 'alltime':
            default: return null
        }
    }

    getDateRangeEnd(dateRange: string): Date | null {
        if (dateRange === 'lastmonth') {
            const now = new Date()
            const end = new Date(now.getFullYear(), now.getMonth(), 0)
            end.setHours(23, 59, 59, 999)
            return end
        }
        if (dateRange === 'custom') {
            const customEnd = this.filterForm.get('customEndDate')?.value
            if (customEnd) {
                const end = new Date(customEnd)
                end.setHours(23, 59, 59, 999)
                return end
            }
        }
        return null
    }
}
